import { DatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import React from 'react';

const { RangePicker } = DatePicker;

// 时间快捷选项（value 用函数，避免模块加载时写死；日范围统一为当天起止）
export const rangePresets: RangePickerProps['presets'] = [
  {
    label: '今日',
    value: () => [dayjs().startOf('day'), dayjs().endOf('day')],
  },
  {
    label: '昨日',
    value: () => [
      dayjs().subtract(1, 'day').startOf('day'),
      dayjs().subtract(1, 'day').endOf('day'),
    ],
  },
  {
    label: '本周',
    value: () => [dayjs().startOf('week'), dayjs().endOf('week')],
  },
  {
    label: '上一周',
    value: () => [
      dayjs().subtract(1, 'week').startOf('week'),
      dayjs().subtract(1, 'week').endOf('week'),
    ],
  },
  {
    label: '近 7 天',
    value: () => [
      dayjs().subtract(6, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  },
  {
    label: '本月',
    value: () => [dayjs().startOf('month'), dayjs().endOf('month')],
  },
  {
    label: '上一月',
    value: () => [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  },
  {
    label: '近 30 天',
    value: () => [
      dayjs().subtract(29, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  },
];

export interface DateRangePickerProps
  extends Omit<RangePickerProps, 'value' | 'onChange' | 'presets'> {
  value?: [Dayjs, Dayjs];
  onChange?: (dates: [Dayjs, Dayjs] | null) => void;
  presets?: RangePickerProps['presets'];
  /** 按 label 排除默认/传入的快捷项，例如 ['近 30 天'] */
  excludePresetLabels?: string[];
}

/**
 * 日期范围选择器组件
 * 包含常用的时间快捷选项
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  presets: customPresets,
  excludePresetLabels,
  format = 'YYYY-MM-DD',
  ...restProps
}) => {
  const handleChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string],
  ) => {
    if (dates && dates[0] && dates[1]) {
      onChange?.([dates[0], dates[1]]);
    } else {
      onChange?.(null);
    }
  };

  const resolvedPresets = (() => {
    const presets = customPresets || rangePresets;
    if (!excludePresetLabels?.length || !presets?.length) {
      return presets;
    }
    const excludeSet = new Set(excludePresetLabels);
    return presets.filter((item) => !excludeSet.has(String(item.label)));
  })();

  return (
    <RangePicker
      value={value}
      onChange={handleChange}
      presets={resolvedPresets}
      format={format}
      {...restProps}
    />
  );
};

export default DateRangePicker;
